import type { AdminSearchTarget, AdminTermKind } from '@/app/shared/services/tracking.service';

/**
 * Nhận diện lời gọi API cần ghi hành vi của nhân viên (AdminAudit pha 3). Chỉ là hàm thuần để dễ kiểm thử; TrackingInterceptor dùng.
 * Không bao giờ trả về nội dung nhân viên gõ: từ khóa tìm kiếm chỉ được phân loại và đo độ dài.
 */
export type RequestPlan =
  | { kind: 'export'; id: string }
  | { kind: 'report'; id: string; filterNames: string[]; rangeDays?: number }
  | { kind: 'search'; target: AdminSearchTarget; termKind: AdminTermKind; termLength: number };

/** Endpoint tìm kiếm của danh sách khách, đơn và sản phẩm trên admin (đường dẫn đã bỏ domain, viết thường). */
const SEARCH_ENDPOINTS: Record<string, AdminSearchTarget> = {
  'user/user/all': 'customer',
  'order/order/query': 'order',
  'product/productadmin/retrieve': 'product',
};

/** Khóa có thể chứa từ khóa tìm kiếm trong body. */
const TERM_KEYS = ['searchTerm', 'keyword', 'search', 'searchText', 'query', 'userName', 'phone', 'phoneNumber', 'orderId', 'sku', 'name', 'fullName'];

/** Khóa phân trang và sắp xếp: không tính là bộ lọc. */
const PAGING_KEYS = new Set(['pageindex', 'pagesize', 'page', 'limit', 'offset', 'skip', 'take', 'orderby', 'sortby', 'sort', 'sortdirection', 'sortorder', 'ascending', 'descending']);

const FROM_KEYS = ['fromdate', 'startdate', 'datefrom', 'from', 'startat'];
const TO_KEYS = ['todate', 'enddate', 'dateto', 'to', 'endat'];

/** Bỏ domain và query, viết thường, thay số và GUID để các lần gọi cùng endpoint gom được. */
export function normalizePath(url: string, baseUrl: string): string {
  let path = url.startsWith(baseUrl) ? url.slice(baseUrl.length) : url.replace(/^https?:\/\/[^/]+\//, '');
  path = path.split('?')[0].split('#')[0].replace(/^\/+/, '').toLowerCase();
  return path
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/g, ':id')
    .replace(/\/\d+(?=\/|$)/g, '/:n');
}

/** Gộp body (đối tượng hoặc chuỗi JSON) và query string thành một đối tượng phẳng để đọc khóa. */
export function readParams(body: unknown, urlWithParams: string): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const query = urlWithParams.split('?')[1];
  if (query) new URLSearchParams(query.split('#')[0]).forEach((v, k) => (out[k] = v));
  let obj: unknown = body;
  if (typeof body === 'string') {
    try {
      obj = JSON.parse(body);
    } catch {
      obj = null;
    }
  }
  if (obj && typeof obj === 'object' && !Array.isArray(obj)) Object.assign(out, obj as Record<string, unknown>);
  return out;
}

export function filterNamesOf(params: Record<string, unknown>): string[] {
  return Object.keys(params).filter((k) => {
    if (PAGING_KEYS.has(k.toLowerCase())) return false;
    const v = params[k];
    if (v === null || v === undefined || v === '' || v === false) return false;
    if (Array.isArray(v) && v.length === 0) return false;
    return true;
  });
}

/** Số ngày giữa mốc đầu và mốc cuối của khoảng ngày trong bộ lọc, nếu có đủ hai mốc hợp lệ. */
export function rangeDaysOf(params: Record<string, unknown>): number | undefined {
  const find = (names: string[]) => {
    const key = Object.keys(params).find((k) => names.includes(k.toLowerCase()));
    const raw = key ? params[key] : undefined;
    if (typeof raw !== 'string' && typeof raw !== 'number') return undefined;
    const ms = new Date(raw as string).getTime();
    return Number.isFinite(ms) ? ms : undefined;
  };
  const from = find(FROM_KEYS);
  const to = find(TO_KEYS);
  if (from === undefined || to === undefined || to < from) return undefined;
  const days = Math.ceil((to - from) / 86_400_000);
  return days <= 3650 ? days : undefined;
}

/** Loại từ khóa: số điện thoại, mã đơn (số điện thoại kèm #), mã SKU, tên (chỉ khi tìm khách), hoặc chữ khác. */
export function classifyTerm(term: string, target?: AdminSearchTarget): AdminTermKind {
  const t = term.trim();
  if (/^0\d{9,10}#\d+$/.test(t)) return 'order_id';
  if (/^(\+?84|0)\d{8,10}$/.test(t.replace(/[\s.-]/g, ''))) return 'phone';
  if (/^\d{8,14}$/.test(t)) return 'sku';
  if (/^\p{L}[\p{L}\s.'-]{1,}$/u.test(t) && /\s/.test(t) && (target === undefined || target === 'customer')) return 'name';
  return 'text';
}

/** Đọc từ khóa từ params. Chỉ trả loại và độ dài, không trả nội dung. */
export function searchTermOf(params: Record<string, unknown>, target?: AdminSearchTarget): { termKind: AdminTermKind; termLength: number } | null {
  for (const key of TERM_KEYS) {
    const v = params[key];
    if (typeof v === 'string' && v.trim().length > 0) return { termKind: classifyTerm(v, target), termLength: v.trim().length };
  }
  return null;
}

/** Số dòng của phản hồi: mảng, hoặc đối tượng có items/data/rows/results, hoặc có tổng số. */
export function rowCountOf(res: unknown): number | undefined {
  if (Array.isArray(res)) return res.length;
  if (!res || typeof res !== 'object') return undefined;
  const r = res as Record<string, unknown>;
  for (const k of ['totalCount', 'total', 'count', 'totalItems']) {
    if (typeof r[k] === 'number' && Number.isFinite(r[k])) return r[k] as number;
  }
  for (const k of ['items', 'data', 'rows', 'results']) {
    if (Array.isArray(r[k])) return (r[k] as unknown[]).length;
  }
  return undefined;
}

export function planRequest(url: string, method: string, body: unknown, urlWithParams: string, baseUrl: string): RequestPlan | null {
  const path = normalizePath(url, baseUrl);
  if (!path) return null;

  if (path.includes('export')) return { kind: 'export', id: path.slice(0, 100) };

  const target = SEARCH_ENDPOINTS[path];
  if (target) {
    const term = searchTermOf(readParams(body, urlWithParams), target);
    return term ? { kind: 'search', target, termKind: term.termKind, termLength: term.termLength } : null;
  }

  if (path.includes('report')) {
    const params = readParams(body, urlWithParams);
    return { kind: 'report', id: path.slice(0, 100), filterNames: filterNamesOf(params), rangeDays: rangeDaysOf(params) };
  }
  return null;
}
